import { Button } from '@ophelos/ui/components/button';
import { Paperclip, X } from 'lucide-react';
import { type ReactNode, useCallback, useRef, useState } from 'react';
import { type AttachedFile, uploadScreenshot } from '@/apps/support/lib/upload-screenshot';

interface UseFileAttachmentsOptions {
  disabled?: boolean;
}

interface UseFileAttachmentsReturn {
  attachments: AttachedFile[];
  clearAttachments: () => void;
  /** Returns image URLs from completed uploads */
  getImageUrls: () => string[];
  /** Whether any attachment is still uploading */
  uploading: boolean;
  /** Rendered preview of attached files — null when empty */
  attachmentPreview: ReactNode;
  /** Rendered paperclip button + hidden file input */
  paperclipButton: ReactNode;
}

export function useFileAttachments({ disabled }: UseFileAttachmentsOptions = {}): UseFileAttachmentsReturn {
  const [attachments, setAttachments] = useState<AttachedFile[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;
    for (const file of Array.from(files)) {
      if (!file.type.startsWith('image/')) continue;
      const entry: AttachedFile = { file, uploading: true };
      setAttachments((prev) => [...prev, entry]);
      try {
        const url = await uploadScreenshot(file);
        setAttachments((prev) => prev.map((a) => (a.file === file ? { ...a, uploading: false, url } : a)));
      } catch (err) {
        setAttachments((prev) =>
          prev.map((a) => (a.file === file ? { ...a, uploading: false, error: err instanceof Error ? err.message : 'Upload failed' } : a))
        );
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleRemoveAttachment = useCallback((file: File) => {
    setAttachments((prev) => prev.filter((a) => a.file !== file));
  }, []);

  const clearAttachments = useCallback(() => setAttachments([]), []);

  const getImageUrls = useCallback(() => attachments.filter((a) => a.url).map((a) => a.url as string), [attachments]);

  const uploading = attachments.some((a) => a.uploading);

  const attachmentPreview =
    attachments.length > 0 ? (
      <div className="flex flex-wrap gap-2 mb-2">
        {attachments.map((a) => (
          <div
            key={a.file.name + a.file.lastModified}
            className="flex items-center gap-1.5 rounded-md border bg-muted/50 px-2 py-1 text-xs"
          >
            <span className="max-w-[120px] truncate">{a.file.name}</span>
            {a.uploading && <span className="text-muted-foreground animate-pulse">uploading…</span>}
            {a.error && <span className="text-destructive">{a.error}</span>}
            {a.url && <span className="text-green-600">attached</span>}
            <button type="button" onClick={() => handleRemoveAttachment(a.file)} className="text-muted-foreground hover:text-foreground">
              <X className="size-3" />
            </button>
          </div>
        ))}
      </div>
    ) : null;

  const paperclipButton = (
    <>
      <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleFileSelect} className="hidden" />
      <Button
        variant="ghost"
        size="icon-sm"
        className="size-7 rounded-full"
        onClick={() => fileInputRef.current?.click()}
        disabled={disabled}
        aria-label="Attach screenshot"
      >
        <Paperclip className="size-4" />
      </Button>
    </>
  );

  return { attachments, clearAttachments, getImageUrls, uploading, attachmentPreview, paperclipButton };
}
